import { useSelector } from 'react-redux';
import { Button, Input, PlusIcon, Subtitle, TrashIcon } from '../../shared';
import { selectLang } from '../../../store/ui/ui.selector';
import { selectAccount } from '../../../store/auth/auth.selector';
import { translate } from '../../../lib/lang.helper';
import { numToStr, getUnitOfSpecie } from '../../../lib/common.helpers';
import { Table } from 'antd';
import { roundNumber } from '../../../entities/util-functions';

const capacityOptions = (capacityUnit: string) => [
  { id: '10', value: '10', label: `10 ${capacityUnit}` },
  { id: '20', value: '20', label: `20 ${capacityUnit}` },
  { id: '25', value: '25', label: `25 ${capacityUnit}` },
  { id: '50', value: '50', label: `50 ${capacityUnit}` },
  { id: '100', value: '100', label: `100 ${capacityUnit}` },
  { id: '200', value: '200', label: `200 ${capacityUnit}` },
  { id: '500', value: '500', label: `500 ${capacityUnit}` },
  { id: '1000', value: '1000', label: `1000 ${capacityUnit}` },
];

interface IBag {
  quantity?: number;
  capacity?: number;
}

interface Props {
  data?: IBag[] | null;
  onChange: (data: IBag[]) => void;
  className?: string;
  disabled?: boolean;
}

const HarvestBagsForm = ({ data, onChange, className, disabled }: Props) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const bags = data ?? [];

  const updateBag = (i: number, key: keyof IBag, value: string) => {
    const tmp = [...bags];
    const numValue = value.length <= 0 ? undefined : Number(value);
    tmp[i] = { ...tmp[i], [key]: numValue };
    onChange(tmp);
  };
  const deleteBag = (i: number) => {
    const tmp = [...bags];
    tmp.splice(i, 1);
    onChange(tmp);
  };
  const addBag = () => {
    onChange([...bags, { quantity: undefined, capacity: undefined }]);
  };

  const totalBags = bags.reduce((p, c) => p + (c.quantity ?? 0), 0);
  const capacityUnit = getUnitOfSpecie(unitType, 'weight_kg');
  const totalAmount = roundNumber(
    bags.reduce((p, c) => p + (c.quantity ?? 0) * (c.capacity ?? 0), 0),
  );

  const columns = [
    {
      key: 'quantity',
      title: translate(lang, 'Number of Bags'),
      render: (_: any, x: IBag, i: number) => (
        <Input
          type='number'
          onChange={e => updateBag(i, 'quantity', e.target.value)}
          value={numToStr(x.quantity)}
          label=''
          placeholder='0'
          disabled={disabled}
        />
      ),
      width: 200,
    },
    {
      key: 'capacity',
      title: `${translate(lang, 'Bag Capacity')} (${capacityUnit})`,
      render: (_: any, x: IBag, i: number) => (
        <>
          <Input
            type='number'
            onChange={e => updateBag(i, 'capacity', e.target.value)}
            value={numToStr(x.capacity)}
            placeholder='Enter or select capacity'
            disabled={disabled}
            list={`capacity-options-${i}`}
            unit={capacityUnit}
          />
          <datalist id={`capacity-options-${i}`}>
            {capacityOptions(capacityUnit).map(opt => (
              <option key={opt.id} value={opt.value} />
            ))}
          </datalist>
        </>
      ),
      width: '100%',
    },
    {
      key: 'actions',
      title: '',
      render: (__: any, _: IBag, i: number) => (
        <Button
          width='full'
          size={0}
          type='bordered'
          color='red'
          className='d-flex justify-center'
          onClick={() => deleteBag(i)}
          disabled={disabled}
        >
          <TrashIcon color='#cc0101' />
        </Button>
      ),
    },
  ];

  return (
    <div className={`${className ?? ''} more-info-form`}>
      <Subtitle color='black-2' align='left' size={5} fontWeight={500}>
        {translate(lang, 'Bags are being harvested')}
      </Subtitle>
      <div className='mb-7'>
        <Table
          rowKey={(_, index) => `bag-${index}`}
          className='table table--units table--small'
          dataSource={bags}
          columns={columns}
          pagination={false}
        />
        <div className='d-flex w-full justify-between'>
          <Button
            width='full'
            size={5}
            type='bordered'
            color='blue'
            className='d-flex justify-center'
            onClick={addBag}
            disabled={disabled}
          >
            <span
              style={{
                display: 'inline-flex',
                width: '20px',
                height: '20px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlusIcon />
            </span>
          </Button>
          <div className='font-weight-600 mr-12'>{`${translate(lang, 'Totals')}: ${totalBags} bags, ${totalAmount} ${capacityUnit}`}</div>
        </div>
      </div>
    </div>
  );
};

export default HarvestBagsForm;
